import React, { Fragment, useMemo, useState } from 'react';
import { safeArray } from '../../components/ErrorBoundary';
import StatusBadge from '../../components/StatusBadge';
import { parseRABillSerialNumber } from '../../utils/parseRABillSerial';
import { stripBrackets } from '../../utils/dateFormat';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifyError, notifySuccess } from '../../utils/notify';
import ProjectNameText from '../../components/ProjectNameText';

const DIVISION_COLORS = { Civil: '#7C3AED', 'E&M': '#3B82F6', Horticulture: '#22C55E' };

const sumReleasedWithheld = (bill) => (
  safeArray(bill?.released_withheld_records).reduce((s, r) => s + Number(r.amount || 0), 0)
);

/** Amount > 0 requires a release date; amount 0 does not. */
const releasedLineValidationError = (releaseDateStr, amount) => {
  if (!Number.isFinite(amount) || amount < 0) return 'Enter a valid amount (0 or greater).';
  if (amount > 0) {
    const d = releaseDateStr && String(releaseDateStr).trim();
    if (!d) return 'Release date is required when amount is greater than zero.';
  }
  return null;
};

/** Per-bill recovery line items (matches RA Financial Details). */
const recoveryParts = (b) => ({
  sd: Number(b.security_deposit || 0),
  gst: Number(b.gst_deduction || 0),
  it: Number(b.income_tax_deduction || 0),
  cess: Number(b.labour_cess || 0),
  addlSd: Number(b.additional_sd_special_items || 0),
  total: Number(b.total_recoveries || 0),
});

/**
 * Read-only analytics for recoveries & withheld amounts (project-wise + bill-wise).
 * Uses the same filtered bill list as the main RA register.
 */
const RARecoveriesWithheldView = ({
  bills = [],
  projects = [],
  showProjectColumn = true,
  onSelectBill,
  onSaveReleasedWithheld,
  formatCurrency,
  formatCurrencyNoSymbol,
  getPaymentClass,
  getStatusClass,
}) => {
  const [releaseDraft, setReleaseDraft] = useState({});
  const [releaseSavingId, setReleaseSavingId] = useState(null);
  const [releaseEditing, setReleaseEditing] = useState(null); // { billId, recordId }
  const [releaseEditForm, setReleaseEditForm] = useState({ release_date: '', amount: '' });
  const [releasePanelOpen, setReleasePanelOpen] = useState(false);

  const fmt = formatCurrency || ((v) => `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(v || 0))}`);
  const fmtPlain = formatCurrencyNoSymbol || ((v) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(v || 0)));

  const projectName = (pid) => {
    const p = projects.find((x) => Number(x.id) === Number(pid));
    return p ? stripBrackets(p.name || '') : `Project ${pid}`;
  };

  const totals = useMemo(() => {
    let totalRecoveries = 0;
    let totalWithheld = 0;
    let actualRecovered = 0;
    let billsWithActual = 0;
    let billsWithWithheld = 0;
    let sd = 0;
    let gst = 0;
    let it = 0;
    let cess = 0;
    let addlSd = 0;
    let totalReleasedWithheld = 0;
    let billsWithReleased = 0;
    let totalPendingWithheld = 0;
    let billsWithPendingWithheld = 0;
    for (const b of safeArray(bills)) {
      const p = recoveryParts(b);
      sd += p.sd;
      gst += p.gst;
      it += p.it;
      cess += p.cess;
      addlSd += p.addlSd;
      totalRecoveries += Number(b.total_recoveries || 0);
      const wh = Number(b.total_withheld || 0);
      totalWithheld += wh;
      if (wh > 0) billsWithWithheld += 1;
      const rel = sumReleasedWithheld(b);
      totalReleasedWithheld += rel;
      if (rel > 0) billsWithReleased += 1;
      const pending = Math.max(0, wh - rel);
      totalPendingWithheld += pending;
      if (pending > 0) billsWithPendingWithheld += 1;
      const ar = b.actual_total_recovered_amount;
      if (ar != null && ar !== '') {
        actualRecovered += Number(ar || 0);
        billsWithActual += 1;
      }
    }
    const n = safeArray(bills).length;
    return {
      totalRecoveries,
      totalWithheld,
      totalPendingWithheld,
      billsWithPendingWithheld,
      totalReleasedWithheld,
      billsWithReleased,
      actualRecovered,
      billsWithActual,
      billsWithWithheld,
      billCount: n,
      sd,
      gst,
      it,
      cess,
      addlSd,
    };
  }, [bills]);

  const isSingleProject = useMemo(
    () => new Set(safeArray(bills).map((b) => b.project_id)).size === 1,
    [bills],
  );

  const singleProjectName = useMemo(() => {
    if (!isSingleProject || !safeArray(bills).length) return '';
    return projectName(safeArray(bills)[0].project_id);
  }, [bills, isSingleProject, projects]);

  const singleProjectComparisonRows = useMemo(() => {
    if (!isSingleProject) return [];
    return safeArray(bills)
      .map((b) => {
        const p = recoveryParts(b);
        const partsSum = p.sd + p.gst + p.it + p.cess + p.addlSd;
        const stored = Number(b.total_recoveries || 0);
        return {
          bill: b,
          partsSum,
          delta: stored - partsSum,
          storedRec: stored,
          _wh: Number(b.total_withheld || 0),
          releasedSum: sumReleasedWithheld(b),
          netPay: Number(b.net_payable ?? b.net_payment_received ?? 0),
          currBill: Number(
            b.net_value_work_done_since_prev_bill ?? b.current_bill_value ?? 0,
          ),
          ...p,
        };
      })
      .sort((a, b) => {
        const da = parseRABillSerialNumber(a.bill);
        const db = parseRABillSerialNumber(b.bill);
        if (da !== db) return da - db;
        return Number(a.bill.id) - Number(b.bill.id);
      });
  }, [bills, isSingleProject]);

  const singleProjectFooter = useMemo(() => {
    if (!singleProjectComparisonRows.length) return null;
    let sd = 0;
    let gst = 0;
    let it = 0;
    let cess = 0;
    let addlSd = 0;
    let stored = 0;
    let wh = 0;
    let relWh = 0;
    let net = 0;
    let curr = 0;
    singleProjectComparisonRows.forEach((r) => {
      sd += r.sd;
      gst += r.gst;
      it += r.it;
      cess += r.cess;
      addlSd += r.addlSd;
      stored += r.storedRec;
      wh += r._wh;
      relWh += r.releasedSum;
      net += r.netPay;
      curr += r.currBill;
    });
    const partsEquiv = sd + gst + it + cess + addlSd;
    const deltaSum = stored - partsEquiv;
    return {
      sd,
      gst,
      it,
      cess,
      addlSd,
      stored,
      deltaSum,
      wh,
      relWh,
      net,
      curr,
    };
  }, [singleProjectComparisonRows]);

  const byProject = useMemo(() => {
    const map = new Map();
    for (const b of safeArray(bills)) {
      const pid = b.project_id;
      if (!map.has(pid)) {
        map.set(pid, {
          projectId: pid,
          projectName: projectName(pid),
          billCount: 0,
          sd: 0,
          gst: 0,
          it: 0,
          cess: 0,
          addlSd: 0,
          totalRecoveries: 0,
          totalWithheld: 0,
          releasedWithheld: 0,
          actualRecovered: 0,
          actualCount: 0,
        });
      }
      const row = map.get(pid);
      const p = recoveryParts(b);
      row.billCount += 1;
      row.sd += p.sd;
      row.gst += p.gst;
      row.it += p.it;
      row.cess += p.cess;
      row.addlSd += p.addlSd;
      row.totalRecoveries += Number(b.total_recoveries || 0);
      row.totalWithheld += Number(b.total_withheld || 0);
      row.releasedWithheld += sumReleasedWithheld(b);
      const ar = b.actual_total_recovered_amount;
      if (ar != null && ar !== '') {
        row.actualRecovered += Number(ar || 0);
        row.actualCount += 1;
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      const sa = a.totalRecoveries + a.totalWithheld;
      const sb = b.totalRecoveries + b.totalWithheld;
      return sb - sa;
    });
  }, [bills, projects]);

  const billRows = useMemo(() => {
    return safeArray(bills)
      .filter((b) => Number(b.total_withheld || 0) > 0)
      .map((b) => {
        const p = recoveryParts(b);
        return {
          ...b,
          _rec: p.total,
          _wh: Number(b.total_withheld || 0),
          _ar: b.actual_total_recovered_amount != null && b.actual_total_recovered_amount !== ''
            ? Number(b.actual_total_recovered_amount || 0)
            : null,
          _sd: p.sd,
          _gst: p.gst,
          _it: p.it,
          _cess: p.cess,
          _addlSd: p.addlSd,
          _released: sumReleasedWithheld(b),
        };
      })
      .sort((a, b) => (b._rec + b._wh) - (a._rec + a._wh));
  }, [bills]);

  const billsSortedForRelease = useMemo(
    () => [...safeArray(bills)]
      .filter((b) => Number(b.total_withheld || 0) > 0)
      .sort((a, b) => parseRABillSerialNumber(a) - parseRABillSerialNumber(b) || Number(a.id) - Number(b.id)),
    [bills],
  );

  const setReleaseField = (billId, field, value) => {
    setReleaseDraft((prev) => ({
      ...prev,
      [billId]: { ...prev[billId], [field]: value },
    }));
  };

  const handleAddReleasedWithheld = async (bill) => {
    if (!onSaveReleasedWithheld) return;
    const d = releaseDraft[bill.id] || {};
    if (d.amount === '' || d.amount == null) {
      notifyError('Enter an amount (0 is allowed).');
      return;
    }
    const amt = Number(d.amount);
    const err = releasedLineValidationError(d.release_date, amt);
    if (err) {
      notifyError(err);
      return;
    }
    const dateVal = (d.release_date && String(d.release_date).trim()) ? d.release_date : '';
    setReleaseSavingId(bill.id);
    try {
      const next = [...safeArray(bill.released_withheld_records), {
        id: `rw-${bill.id}-${Date.now()}`,
        release_date: dateVal,
        amount: amt,
      }];
      await onSaveReleasedWithheld({ ...bill, released_withheld_records: next });
      notifySuccess('Released withheld recorded.');
      setReleaseDraft((prev) => ({ ...prev, [bill.id]: { release_date: '', amount: '' } }));
    } catch (e) {
      notifyError('Could not save released withheld.');
    } finally {
      setReleaseSavingId(null);
    }
  };

  const startEditRelease = (bill, rec) => {
    setReleaseEditing({ billId: bill.id, recordId: rec.id });
    setReleaseEditForm({
      release_date: rec.release_date || '',
      amount: rec.amount != null && rec.amount !== '' ? String(rec.amount) : '',
    });
  };

  const cancelEditRelease = () => {
    setReleaseEditing(null);
    setReleaseEditForm({ release_date: '', amount: '' });
  };

  const handleSaveEditedRelease = async (bill) => {
    if (!onSaveReleasedWithheld || !releaseEditing || releaseEditing.billId !== bill.id) return;
    const { recordId } = releaseEditing;
    if (releaseEditForm.amount === '' || releaseEditForm.amount == null) {
      notifyError('Enter an amount (0 is allowed).');
      return;
    }
    const amt = Number(releaseEditForm.amount);
    const err = releasedLineValidationError(releaseEditForm.release_date, amt);
    if (err) {
      notifyError(err);
      return;
    }
    const dateVal = (releaseEditForm.release_date && String(releaseEditForm.release_date).trim())
      ? releaseEditForm.release_date
      : '';
    setReleaseSavingId(bill.id);
    try {
      const next = safeArray(bill.released_withheld_records).map((r) => (
        String(r.id) === String(recordId)
          ? { ...r, release_date: dateVal, amount: amt }
          : r
      ));
      await onSaveReleasedWithheld({ ...bill, released_withheld_records: next });
      notifySuccess('Release entry updated.');
      cancelEditRelease();
    } catch (e) {
      notifyError('Could not save released withheld.');
    } finally {
      setReleaseSavingId(null);
    }
  };

  const handleDeleteRelease = async (bill, recordId) => {
    if (!onSaveReleasedWithheld) return;
    const raLabel = bill.ra_ref || bill.ra_no || 'this RA';
    const ok = await confirmDialog(
      `Remove this released withheld line for ${raLabel}? Released withheld totals on the bill will update.`,
      'Delete release entry',
    );
    if (!ok) return;
    setReleaseSavingId(bill.id);
    try {
      const next = safeArray(bill.released_withheld_records).filter(
        (r) => String(r.id) !== String(recordId),
      );
      await onSaveReleasedWithheld({ ...bill, released_withheld_records: next });
      notifySuccess('Release entry removed.');
      if (releaseEditing?.billId === bill.id && String(releaseEditing.recordId) === String(recordId)) {
        cancelEditRelease();
      }
    } catch (e) {
      notifyError('Could not save released withheld.');
    } finally {
      setReleaseSavingId(null);
    }
  };

  if (!safeArray(bills).length) {
    return (
      <div className="ra-rw">
        <div className="ra-rw-intro">
          <h3>Recoveries &amp; withheld</h3>
          <p>No RA bills match the current filters. Adjust project or status filters on the register to see recoveries and withheld breakdowns.</p>
        </div>
        <div className="ra-rw-empty">No data to display.</div>
      </div>
    );
  }

  return (
    <div className="ra-rw">
      <div className="ra-rw-intro">
        <h3>Recoveries &amp; withheld</h3>
        <p>
          Aggregates <strong>total recoveries</strong> (SD, GST, IT, cess, addl. SD) and <strong>total withheld</strong> (milestone, MPR, other) from each RA bill.
          Amounts respect the same filters as the RA register. Summary KPIs are on the <strong>RA register</strong> tab. Open a bill from the table below to edit details.
        </p>
      </div>

      {isSingleProject ? (
        <>
          <div className="ra-rw-calc-note">
            <p>
              <strong>How recoveries are calculated (each RA bill):</strong>{' '}
              <strong>Total recoveries</strong> = Security deposit + GST + Income tax + Labour cess + Addl. SD (each rounded). Project RA rates apply; the deduction base follows the RA form (current bill value / 5(a) for SD; taxable base for GST, IT, cess — same as <strong>Financial Details</strong> when the bill is saved).
            </p>
            <p>
              <strong>This table:</strong> <strong>Σ Rec. (stored)</strong> is the saved <code>total_recoveries</code>. <strong>Δ</strong> = stored − (SD + GST + IT + Cess + Addl. SD) (normally 0; otherwise rounding or manual edits). <strong>Released</strong> is the sum of amounts entered under <em>Record released withhelds</em> (expand the panel at the bottom of this tab).
            </p>
          </div>
          <div className="ra-rw-section-title">
            <h4>
              Full comparison —{' '}
              <ProjectNameText as="span">{singleProjectName || 'Project'}</ProjectNameText>
            </h4>
            <span className="ra-rw-meta">All RA bills for this project (by RA no.)</span>
          </div>
          <div className="ra-rw-panel">
            <div className="ra-rw-table-wrap ra-rw-table-wrap--tall">
              <table className="ra-rw-table">
                <thead>
                  <tr>
                    <th>RA ref</th>
                    <th>Div</th>
                    <th className="ra-rw-num" title="Net value of work done since previous bill (line 5a)">Curr. (5a) ₹</th>
                    <th className="ra-rw-num ra-rw-th-rec">SD ₹</th>
                    <th className="ra-rw-num ra-rw-th-rec">GST ₹</th>
                    <th className="ra-rw-num ra-rw-th-rec">IT ₹</th>
                    <th className="ra-rw-num ra-rw-th-rec">Cess ₹</th>
                    <th className="ra-rw-num ra-rw-th-rec">Addl. SD ₹</th>
                    <th className="ra-rw-num" title="Stored total recoveries">Σ Rec.</th>
                    <th className="ra-rw-num" title="Stored minus sum of SD…Addl.">Δ</th>
                    <th className="ra-rw-num">Withheld</th>
                    <th className="ra-rw-num" title="Released withheld amounts">Released (₹)</th>
                    <th className="ra-rw-num">Net payable</th>
                    <th>Payment</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {singleProjectComparisonRows.map((row) => (
                    <tr
                      key={row.bill.id}
                      style={{ cursor: onSelectBill ? 'pointer' : 'default' }}
                      onClick={() => onSelectBill && onSelectBill(row.bill)}
                    >
                      <td style={{ fontWeight: 600, color: '#6D28D9' }}>{row.bill.ra_ref || row.bill.ra_no || '—'}</td>
                      <td style={{ color: row.bill.division ? (DIVISION_COLORS[row.bill.division] || '#374151') : '#94a3b8', fontSize: 12 }}>
                        {row.bill.division || '—'}
                      </td>
                      <td className="ra-rw-num">{fmtPlain(row.currBill)}</td>
                      <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(row.sd)}</td>
                      <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(row.gst)}</td>
                      <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(row.it)}</td>
                      <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(row.cess)}</td>
                      <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(row.addlSd)}</td>
                      <td className="ra-rw-num" style={{ color: '#0f766e', fontWeight: 600 }}>{fmtPlain(row.storedRec)}</td>
                      <td className="ra-rw-num" style={{ color: Math.abs(row.delta) >= 1 ? '#b45309' : '#64748b' }}>{fmtPlain(row.delta)}</td>
                      <td className="ra-rw-num" style={{ color: '#9a3412' }}>{fmtPlain(row._wh)}</td>
                      <td className="ra-rw-num" style={{ color: '#0f766e' }}>{fmtPlain(row.releasedSum)}</td>
                      <td className="ra-rw-num">{fmtPlain(row.netPay)}</td>
                      <td><StatusBadge tone={getPaymentClass(row.bill.payment_status)}>{row.bill.payment_status || '—'}</StatusBadge></td>
                      <td><StatusBadge tone={getStatusClass(row.bill.status)}>{row.bill.status || '—'}</StatusBadge></td>
                    </tr>
                  ))}
                </tbody>
                {singleProjectFooter && (
                  <tfoot>
                    <tr className="ra-rw-tfoot-total">
                      <td colSpan={2} style={{ fontWeight: 700 }}>Total</td>
                      <td className="ra-rw-num">{fmtPlain(singleProjectFooter.curr)}</td>
                      <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(singleProjectFooter.sd)}</td>
                      <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(singleProjectFooter.gst)}</td>
                      <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(singleProjectFooter.it)}</td>
                      <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(singleProjectFooter.cess)}</td>
                      <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(singleProjectFooter.addlSd)}</td>
                      <td className="ra-rw-num" style={{ color: '#0f766e' }}>{fmtPlain(singleProjectFooter.stored)}</td>
                      <td className="ra-rw-num">{fmtPlain(singleProjectFooter.deltaSum)}</td>
                      <td className="ra-rw-num" style={{ color: '#9a3412' }}>{fmtPlain(singleProjectFooter.wh)}</td>
                      <td className="ra-rw-num" style={{ color: '#0f766e' }}>{fmtPlain(singleProjectFooter.relWh)}</td>
                      <td className="ra-rw-num">{fmtPlain(singleProjectFooter.net)}</td>
                      <td />
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {onSelectBill && (
              <p className="ra-rw-click-hint" style={{ padding: '0 14px 14px' }}>
                Click a row to open the RA bill. Use Financial Details to see or edit inputs.
              </p>
            )}
          </div>
        </>
      ) : (
        <>
      <div className="ra-rw-section-title">
        <h4>By project</h4>
        <span className="ra-rw-meta">{byProject.length} project(s)</span>
      </div>
      <div className="ra-rw-panel">
        <div className="ra-rw-table-wrap">
          <table className="ra-rw-table">
            <thead>
              <tr>
                <th>Project</th>
                <th className="ra-rw-num">Bills</th>
                <th className="ra-rw-num ra-rw-th-rec" title="Security deposit">SD (₹)</th>
                <th className="ra-rw-num ra-rw-th-rec" title="GST deduction">GST (₹)</th>
                <th className="ra-rw-num ra-rw-th-rec" title="Income tax">IT (₹)</th>
                <th className="ra-rw-num ra-rw-th-rec" title="Labour cess">Cess (₹)</th>
                <th className="ra-rw-num ra-rw-th-rec" title="Additional SD — special items">Addl. SD (₹)</th>
                <th className="ra-rw-num" title="Total recoveries">Σ Rec. (₹)</th>
                <th className="ra-rw-num">Withheld (₹)</th>
                <th className="ra-rw-num" title="Released withhelds">Rel. WH (₹)</th>
                <th className="ra-rw-num">Actual (₹)</th>
              </tr>
            </thead>
            <tbody>
              {byProject.map((row) => (
                  <tr key={row.projectId}>
                    <td>
                      <ProjectNameText as="span">{row.projectName}</ProjectNameText>
                    </td>
                    <td className="ra-rw-num">{row.billCount}</td>
                    <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(row.sd)}</td>
                    <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(row.gst)}</td>
                    <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(row.it)}</td>
                    <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(row.cess)}</td>
                    <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(row.addlSd)}</td>
                    <td className="ra-rw-num" style={{ color: '#0f766e', fontWeight: 600 }}>{fmtPlain(row.totalRecoveries)}</td>
                    <td className="ra-rw-num" style={{ color: '#9a3412' }}>{fmtPlain(row.totalWithheld)}</td>
                    <td className="ra-rw-num" style={{ color: '#0f766e' }}>{fmtPlain(row.releasedWithheld)}</td>
                    <td className="ra-rw-num">{row.actualCount > 0 ? fmtPlain(row.actualRecovered) : '—'}</td>
                  </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="ra-rw-tfoot-total">
                <td style={{ fontWeight: 700 }}>Total (filtered)</td>
                <td className="ra-rw-num">{totals.billCount}</td>
                <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(totals.sd)}</td>
                <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(totals.gst)}</td>
                <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(totals.it)}</td>
                <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(totals.cess)}</td>
                <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(totals.addlSd)}</td>
                <td className="ra-rw-num" style={{ color: '#0f766e' }}>{fmtPlain(totals.totalRecoveries)}</td>
                <td className="ra-rw-num" style={{ color: '#9a3412' }}>{fmtPlain(totals.totalWithheld)}</td>
                <td className="ra-rw-num" style={{ color: '#0f766e' }}>{fmtPlain(totals.totalReleasedWithheld)}</td>
                <td className="ra-rw-num">{totals.billsWithActual > 0 ? fmtPlain(totals.actualRecovered) : '—'}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="ra-rw-section-title">
        <h4>By bill</h4>
        <span className="ra-rw-meta">{billRows.length} row(s) · click a row to open RA detail</span>
      </div>
      <div className="ra-rw-panel">
        <div className="ra-rw-table-wrap ra-rw-table-wrap--tall">
          <table className="ra-rw-table">
            <thead>
              <tr>
                <th>RA ref</th>
                {showProjectColumn && <th>Project</th>}
                <th>Division</th>
                <th className="ra-rw-num ra-rw-th-rec" title="Security deposit">SD (₹)</th>
                <th className="ra-rw-num ra-rw-th-rec" title="GST">GST (₹)</th>
                <th className="ra-rw-num ra-rw-th-rec" title="Income tax">IT (₹)</th>
                <th className="ra-rw-num ra-rw-th-rec" title="Labour cess">Cess (₹)</th>
                <th className="ra-rw-num ra-rw-th-rec" title="Addl. SD">Addl. SD (₹)</th>
                <th className="ra-rw-num" title="Total recoveries">Σ Rec. (₹)</th>
                <th className="ra-rw-num">Withheld (₹)</th>
                <th className="ra-rw-num">Rel. WH (₹)</th>
                <th className="ra-rw-num">Actual (₹)</th>
                <th>Payment</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {billRows.length === 0 ? (
                <tr>
                  <td colSpan={showProjectColumn ? 14 : 13} style={{ textAlign: 'center', padding: 16, color: '#6b7280' }}>
                    No bills with withheld amount in current filter.
                  </td>
                </tr>
              ) : billRows.map((bill) => (
                <tr
                  key={bill.id}
                  style={{ cursor: onSelectBill ? 'pointer' : 'default' }}
                  onClick={() => onSelectBill && onSelectBill(bill)}
                >
                  <td style={{ fontWeight: 600, color: '#6D28D9' }}>{bill.ra_ref || bill.ra_no || '—'}</td>
                  {showProjectColumn && (
                    <td>
                      <ProjectNameText as="span">{projectName(bill.project_id)}</ProjectNameText>
                    </td>
                  )}
                  <td style={{ color: bill.division ? (DIVISION_COLORS[bill.division] || '#374151') : '#94a3b8' }}>
                    {bill.division || '—'}
                  </td>
                  <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(bill._sd)}</td>
                  <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(bill._gst)}</td>
                  <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(bill._it)}</td>
                  <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(bill._cess)}</td>
                  <td className="ra-rw-num ra-rw-cell-rec">{fmtPlain(bill._addlSd)}</td>
                  <td className="ra-rw-num" style={{ color: '#0f766e', fontWeight: 600 }}>{fmtPlain(bill._rec)}</td>
                  <td className="ra-rw-num" style={{ color: '#9a3412' }}>{fmtPlain(bill._wh)}</td>
                  <td className="ra-rw-num" style={{ color: '#0f766e' }}>{fmtPlain(bill._released)}</td>
                  <td className="ra-rw-num">{bill._ar == null ? '—' : fmtPlain(bill._ar)}</td>
                  <td><StatusBadge tone={getPaymentClass(bill.payment_status)}>{bill.payment_status || '—'}</StatusBadge></td>
                  <td><StatusBadge tone={getStatusClass(bill.status)}>{bill.status || '—'}</StatusBadge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {onSelectBill && (
          <p className="ra-rw-click-hint" style={{ padding: '0 14px 14px' }}>
            Tip: use the Financial Details tab on the bill to adjust recoveries breakdown and withheld line items.
          </p>
        )}
      </div>
        </>
      )}

      {onSaveReleasedWithheld && safeArray(bills).length > 0 && (
        <div className={`ra-rw-release-panel-wrap${releasePanelOpen ? ' ra-rw-release-panel-wrap--open' : ''}`}>
          <button
            type="button"
            className="ra-rw-release-collapse-trigger"
            aria-expanded={releasePanelOpen}
            onClick={() => setReleasePanelOpen((v) => !v)}
          >
            <span className="ra-rw-release-collapse-trigger-label">
              <strong>Record released withhelds</strong>
              <span className="ra-rw-release-collapse-trigger-meta">
                {releasePanelOpen
                  ? 'Table below — same data as Released withhelds KPI on the main register tab.'
                  : 'Collapsed by default — click Show when you need to add or edit release lines.'}
              </span>
            </span>
            <span className="ra-rw-release-collapse-trigger-chevron" aria-hidden>
              {releasePanelOpen ? 'Hide ▲' : 'Show ▼'}
            </span>
          </button>
          {releasePanelOpen && (
              <div className="ra-rw-panel ra-rw-panel--release-table">
                <p className="ra-rw-release-panel-intro ra-rw-meta">
                  Release date required only when amount &gt; 0; edit or remove saved lines below each bill.
                </p>
                <div className="ra-rw-table-wrap">
                  <table className="ra-rw-table">
                    <thead>
                      <tr>
                        <th>RA ref</th>
                        {showProjectColumn && <th>Project</th>}
                        <th className="ra-rw-num">Withheld (₹)</th>
                        <th className="ra-rw-num">Released so far (₹)</th>
                        <th>Release date</th>
                        <th className="ra-rw-num">Amount (₹)</th>
                        <th style={{ width: 120 }}> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {billsSortedForRelease.length === 0 ? (
                        <tr>
                          <td colSpan={showProjectColumn ? 7 : 6} style={{ textAlign: 'center', padding: 16, color: '#6b7280' }}>
                            No bills with withheld amount in current filter.
                          </td>
                        </tr>
                      ) : billsSortedForRelease.map((bill) => {
                        const d = releaseDraft[bill.id] || { release_date: '', amount: '' };
                        const records = safeArray(bill.released_withheld_records);
                        const colspan = showProjectColumn ? 7 : 6;
                        const addAmtParsed = d.amount === '' || d.amount == null ? null : Number(d.amount);
                        const addDateRequired = addAmtParsed != null && Number.isFinite(addAmtParsed) && addAmtParsed > 0;
                        return (
                          <Fragment key={bill.id}>
                            <tr>
                              <td style={{ fontWeight: 600, color: '#6D28D9' }}>{bill.ra_ref || bill.ra_no || '—'}</td>
                              {showProjectColumn && (
                                <td>
                                  <ProjectNameText as="span">{projectName(bill.project_id)}</ProjectNameText>
                                </td>
                              )}
                              <td className="ra-rw-num" style={{ color: '#9a3412' }}>{fmtPlain(Number(bill.total_withheld || 0))}</td>
                              <td className="ra-rw-num" style={{ color: '#0f766e', fontWeight: 600 }}>{fmtPlain(sumReleasedWithheld(bill))}</td>
                              <td>
                                <input
                                  type="date"
                                  className="ra-rw-release-input"
                                  value={d.release_date || ''}
                                  required={addDateRequired}
                                  aria-required={addDateRequired}
                                  onChange={(e) => setReleaseField(bill.id, 'release_date', e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td className="ra-rw-num">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="ra-rw-release-input ra-rw-release-input--num"
                                  placeholder="0"
                                  value={d.amount ?? ''}
                                  onChange={(e) => setReleaseField(bill.id, 'amount', e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="btn-primary btn-sm"
                                  disabled={releaseSavingId === bill.id}
                                  onClick={() => handleAddReleasedWithheld(bill)}
                                >
                                  {releaseSavingId === bill.id ? 'Saving…' : 'Add release'}
                                </button>
                              </td>
                            </tr>
                            {records.length > 0 && (
                              <tr className="ra-rw-subrow">
                                <td colSpan={colspan}>
                                  <div className="ra-rw-release-lines-label">Saved releases</div>
                                  <div className="ra-rw-release-lines">
                                    {records.map((rec) => {
                                      const isEditing = releaseEditing?.billId === bill.id
                                        && String(releaseEditing.recordId) === String(rec.id);
                                      const busy = releaseSavingId === bill.id;
                                      return (
                                        <div key={rec.id} className="ra-rw-release-line">
                                          {isEditing ? (
                                            <>
                                              <input
                                                type="date"
                                                className="ra-rw-release-input"
                                                value={releaseEditForm.release_date}
                                                required={
                                                  releaseEditForm.amount !== '' && releaseEditForm.amount != null
                                                  && Number(releaseEditForm.amount) > 0
                                                }
                                                onChange={(e) => setReleaseEditForm((f) => ({
                                                  ...f,
                                                  release_date: e.target.value,
                                                }))}
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                              <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className="ra-rw-release-input ra-rw-release-input--num"
                                                value={releaseEditForm.amount}
                                                onChange={(e) => setReleaseEditForm((f) => ({
                                                  ...f,
                                                  amount: e.target.value,
                                                }))}
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                              <button
                                                type="button"
                                                className="btn-primary btn-sm"
                                                disabled={busy}
                                                onClick={() => handleSaveEditedRelease(bill)}
                                              >
                                                {busy ? 'Saving…' : 'Save'}
                                              </button>
                                              <button
                                                type="button"
                                                className="btn-secondary btn-sm"
                                                disabled={busy}
                                                onClick={cancelEditRelease}
                                              >
                                                Cancel
                                              </button>
                                            </>
                                          ) : (
                                            <>
                                              <span className="ra-rw-release-line-date">
                                                {rec.release_date && String(rec.release_date).trim()
                                                  ? rec.release_date
                                                  : '—'}
                                              </span>
                                              <span className="ra-rw-release-line-amt">{fmtPlain(Number(rec.amount || 0))}</span>
                                              <button
                                                type="button"
                                                className="btn-secondary btn-sm"
                                                disabled={busy}
                                                onClick={() => startEditRelease(bill, rec)}
                                              >
                                                Edit
                                              </button>
                                              <button
                                                type="button"
                                                className="btn-secondary btn-sm"
                                                disabled={busy}
                                                onClick={() => handleDeleteRelease(bill, rec.id)}
                                              >
                                                Delete
                                              </button>
                                            </>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="ra-rw-click-hint" style={{ padding: '0 14px 14px' }}>
                  Each line adds one release entry. Totals match the <strong>Released withhelds</strong> KPI on the main RA register tab.
                </p>
              </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RARecoveriesWithheldView;
